import {
    Box,
    FormLabel,
    Stack,
    VStack,
    FormControl,
    Text,
} from '@chakra-ui/react';
import { Select } from 'chakra-react-select';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from 'hooks/useCustomQuery';

import { axios } from '../../../../services/axios';

import { AlertComponent, LoadingIndicator } from 'components/v4';
import FormBase from '@/components/forms/new/FormBase';

import { chakraSelectDarkMode, defaultChakraSelectStyle } from '@/constants';
import {
    stackSpacing,
    formLabelStyling,
    stackStyling,
    descriptionStyling,
} from 'components/forms/new/formStyles';
import FieldToolTip from 'components/forms/new/FieldToolTip';
import BreadcrumbTrail from 'components/BreadcrumbTrail';
import walkthroughIds from '../../walkthroughIds';
import { mappedServiceDisplayName } from 'utils/utils';

export default function SelectProfile({
    handleSelectServiceProfileType,
    level,
    handleBackButton,
    filterFn,
    title = 'Select Service Profile Type',
}) {
    const { currentWholesaler: wholesalerID, currentPartner: partnerID } =
        useSelector((state) => state.navigationLists);
    const { requiredScope } = useSelector((state) => state.login);
    const { darkMode } = useSelector((state) => state.settings);

    const [isHovered, setIsHovered] = useState(false);

    const { serviceProfiles } = walkthroughIds;
    const chakraSelectDark = chakraSelectDarkMode(darkMode);

    const scope =
        level === 'partner'
            ? `Partner/${partnerID}`
            : `Wholesaler/${wholesalerID}`;

    const {
        data: dropdown = [],
        isLoading,
        error,
    } = useQuery({
        queryKey: ['serviceProfileTypes', level, partnerID, wholesalerID],
        queryFn: async () => {
            const result = await axios.get(
                `/ServiceProfiles/${scope}/ServiceTypes/Available`,
                { headers: { 'X-RequestScope': requiredScope } },
            );
            return result.data;
        },
        select: filterFn,
        staleTime: 0,
    });

    const hasAlert = Boolean(error) || !dropdown?.length;

    const handleServiceSelect = (service) => {
        // Immediately proceed with selection for better UX
        handleSelectServiceProfileType(service.id, service.displayName);
    };

    if (isLoading) {
        return <LoadingIndicator />;
    }

    const breadCrumbTrail = [
        { label: 'Services', path: ['', { index: 0 }] },
        { label: 'Service Profiles', path: '' },
    ];

    return (
        <>
            <BreadcrumbTrail
                breadCrumbTrail={breadCrumbTrail}
                handleBackButton={handleBackButton}
            />
            <FormBase
                header={
                    <Text fontSize="20" fontWeight="500" marginBottom="15px">
                        {title}
                    </Text>
                }
                subTitleHeader={true}
                backLink={['/service-profiles', { state: { index: 0 } }]}
                customClick={true}
                onBack={handleBackButton}
                isLoading={false}
                hideSubmitButton={true}
                hideSaveButton={true}
                hideFooter={false}
                showSubmitButton={false}
                initialValues={{}}>
                {() => (
                    <>
                        {hasAlert ? (
                            <Box mb={4}>
                                <AlertComponent
                                    status={error ? 'error' : 'info'}
                                    description={
                                        error
                                            ? error?.response?.data?.message ||
                                              'An error occurred. Please try again later.'
                                            : 'There is no Service Profile Type available to add. Please come back later.'
                                    }
                                />
                            </Box>
                        ) : (
                            <FormControl
                                sx={{ margin: '0 !important' }}
                                isRequired={true}
                                onMouseEnter={() => setIsHovered(true)}
                                onMouseLeave={() => setIsHovered(false)}>
                                <Stack
                                    direction="row"
                                    spacing={stackSpacing}
                                    sx={stackStyling(true)}>
                                    <Box>
                                        <FormLabel
                                            sx={formLabelStyling(darkMode)}>
                                            Service Profile Type
                                        </FormLabel>
                                        <Text
                                            sx={descriptionStyling}
                                            maxWidth={250}>
                                            Please select a Service Profile Type
                                            to add.
                                        </Text>
                                    </Box>
                                    <Box flex="1" minWidth="450px">
                                        <VStack
                                            width="100%"
                                            alignItems="normal"
                                            marginTop="8px">
                                            <Select
                                                placeholder="Select a Service Profile Type"
                                                options={[...dropdown].sort(
                                                    (a, b) =>
                                                        mappedServiceDisplayName(
                                                            a.displayName,
                                                        ).localeCompare(
                                                            mappedServiceDisplayName(
                                                                b.displayName,
                                                            ),
                                                        ),
                                                )}
                                                getOptionValue={(e) => e.id}
                                                getOptionLabel={(e) =>
                                                    mappedServiceDisplayName(
                                                        e.displayName,
                                                    )
                                                }
                                                onChange={handleServiceSelect}
                                                chakraStyles={{
                                                    ...defaultChakraSelectStyle,
                                                    ...chakraSelectDark,
                                                }}
                                                data-walkthroughid={
                                                    serviceProfiles.ufSelectProfileType
                                                }
                                                selectedOptionColor="brand"
                                                menuPortalTarget={
                                                    typeof document !==
                                                    'undefined'
                                                        ? document.body
                                                        : undefined
                                                }
                                                styles={{
                                                    menuPortal: (base) => ({
                                                        ...base,
                                                        zIndex: 9999,
                                                    }),
                                                }}
                                            />
                                        </VStack>
                                    </Box>
                                    {isHovered && (
                                        <FieldToolTip
                                            label="Choose the type of service profile you want to create"
                                            ariaLabel="service-profile-type-tip"
                                            left="25px"
                                            top="25px"
                                        />
                                    )}
                                </Stack>
                            </FormControl>
                        )}
                    </>
                )}
            </FormBase>
        </>
    );
}
